import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { format, addDays } from 'date-fns';

// Context.
import { OmaLummeApp } from '../../../omaLummeContext';
import { ManageContractsApp } from './contractManagementContext';

// Components.
import Box from '../../Common/Box';
import SubMenu from '../../Common/SubMenu';
import Loading from '../../Common/Loading';
import CustomerDetails from '../../MyInfo/CustomerDetails';

import MoveBase from './forms/MoveBase';
import New from './forms/New';
import Terminate from "./forms/Terminate";
import ChangeBase from './forms/ChangeBase';

import Start from './sections/Start';
import Footer from './sections/Footer';
import ManageContractsSummary from './ManageContractsSummary';
import ThankYou from './sections/ThankYou';

import { ManageContractsCheckbox } from './ManageContractsRadioCheckbox';

// Actions.
import { getCustomerDetails, getCustomerSSN, getMarketingPermissions, postContractManagement, putPaymentType } from '../../../actions';
// Types.
import {
  Contract,
  ContractManagementFormObject,
  ContractManagementFrendsAdditionalOwner,
  ContractManagementFrendsAddress,
  OmaLummeTariff,
  OmaLummeTariffElement,
} from '../../../types/contract';
import { SimpleUser } from '../../../types/customer';
// Utils.
import { gaEventIfAllowed, gaPageviewIfAllowed } from '../../../utils/googleAnalytics';

import {
  fetchCampaignOmaLummePrices,
  validateContractManagementForm,
  paymentTypeToNumber,
  selectedFormToUseCase,
  createContractManagementFrendsAddress,
  extraServicesAvailable,
  tariffsUpgradableToGreenEnergy,
  bankNameToId,
  validateAddress,
} from '../../../utils/contractManagement';

const productionGroupsToBeFiltered = [6, 7, 9, 21, 22];

export const filterContracts = (contracts: Contract[]) =>
  contracts.filter((contract) => {
    if (
      (contract.endDate && new Date(contract.endDate) <= new Date()) || // contract end date is now or in the past
      contract.contractStatus.contractStatusNo.toLowerCase() === 's' || // contract is settled
      contract.contractStatus.contractStatusNo.toLowerCase() === 'm' || // contract is potential
      contract.contractStatus.contractStatusNo.toLowerCase() === 'o' || // contract is ended but not invoiced
      contract.contractType.contractTypeNo !== 0 ||
      productionGroupsToBeFiltered.includes(contract.contractGroup.contractGroupNo) // contract is production
    ) {
      return false;
    } else {
      return true;
    }
  });

const OomiLockDownDate = new Date("2026-05-25T00:00:00Z");
// const OomiLockDownDate = new Date('2026-08-31T00:00:00Z');
const currentDate = new Date();
export const disableManageContracts = currentDate >= OomiLockDownDate;

export const ManageContracts: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = ManageContractsApp.useDispatch();

  const { users, contracts, ledgers } = OmaLummeApp.useState();

  const activeUser = users.activeUser;
  const { customerType } = activeUser;
  const [pageview, setPageview] = React.useState<boolean>(false);

  const customerFetched = users.activeUser.userName !== '' ? true : false;

  const {
    ssn,
    filteredContracts,
    filteredLedgers,
    selectedForm,
    selectedForm2,
    selectedForm4,
    formPage,
    oldContract,
    oldContractOwner,
    endDate,
    startDate,
    createNewContract,
    newAddress,
    primaryAddress,
    changeAddressDate,
    terminateLedgerUseCurrentAddress,
    terminateLedgerAddress,
    houseType,
    changePrimaryAddress,
    newContract,
    campaignOmaLummePrices,
    addOnTemplates,
    extraServices,
    useVaihtoturva,
    payment,
    multipleOwners,
    multipleOwnersNewAddress,
  } = ManageContractsApp.useState();

  // const [oldContractHasExtraServices, setOldContractHasExtraServices] = React.useState<boolean>(false); // Commented out until extra services can be cancelled
  // const [useVaihtoturva, setUseVaihtoturva] = React.useState<string>(); // Commented out until vaihtoturva is implemented

  const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);
  const [marketingPermissions, setMarketingPermissions] = React.useState<boolean>(false);
  const [marketingEmail, setMarketingEmail] = React.useState<boolean>(false);
  const [marketingSMS, setMarketingSMS] = React.useState<boolean>(false);
  const [formValid, setFormValid] = React.useState<boolean>(false);
  const [formSubmitting, setFormSubmitting] = React.useState<boolean>(false);
  const [fetchingOldContractOwner, setFetchingOldContractOwner] = React.useState<boolean>(false);

  const navigate = useNavigate();

  if (customerType === 'business') {
    navigate('/contracts');
  }

  React.useEffect(() => {
    const filteredContracts = contracts ? filterContracts(contracts) : [];

    dispatch(['filterContracts', filteredContracts]);
  }, [contracts]);

  const filteredPowerContracts = filteredContracts.filter((contract) => contract.contractType.contractTypeName.toLowerCase().includes('power'));

  React.useEffect(() => {
    const filteredLedgerNumbers = [...new Set(filteredContracts.map((item) => item.ledgerNo))];
    const filteredLedgers = ledgers ? ledgers.filter((ledger) => filteredLedgerNumbers.includes(ledger.ledgerNo)) : [];

    dispatch(['filterLedgers', filteredLedgers]);
  }, [ledgers, filteredContracts]);

  React.useEffect(() => {
    if (!pageview) {
      gaPageviewIfAllowed('/contracts/manage');
      setPageview(true);
    }
  }, [pageview]);

  // Browser back button functionality

  React.useEffect(() => {
    if (window.location.hash) {
      navigate(window.location.pathname);
    }
  }, []);

  window.addEventListener('hashchange', function () {
    if (window.location.hash === '#thanks') {
      dispatch(['changePage', 2]);
    } else if (window.location.hash === '#summary') {
      dispatch(['changePage', 1]);
    } else {
      dispatch(['changePage', 0]);
    }
  });

  // Update list of new contract options

  React.useEffect(() => {
    if (!(selectedForm === 4 && selectedForm4 === 3)) {
      const campaignPromise = fetchCampaignOmaLummePrices(import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_CAMPAIGN_ID);
      campaignPromise.then((campaigns) => {
        dispatch(['changeCampaigns', campaigns]);
      });
    }
  }, [selectedForm, selectedForm4]);

  const currentCampaigns = [...new Set(campaignOmaLummePrices.map((item) => item.cisCampaignId))];

  React.useEffect(() => {
    async function fetchCustomerSSN(): Promise<void> {
      const customerId = oldContractOwner && oldContractOwner.userId !== activeUser.userId ? oldContractOwner.userId : activeUser.userId;

      const ssn = await getCustomerSSN(customerId);

      dispatch(['changeSSN', ssn]);
    }

    if (activeUser) {
      fetchCustomerSSN();
    }
  }, [activeUser, oldContractOwner]);

  React.useEffect(() => {
    if (!(selectedForm === 4 && selectedForm4 === 3) && !useVaihtoturva) {
      const campaignId = import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_CAMPAIGN_ID;

      if (!currentCampaigns.includes(parseInt(campaignId))) {
        const campaignPromise = fetchCampaignOmaLummePrices(campaignId);
        campaignPromise.then((campaigns) => {
          dispatch(['changeCampaigns', campaigns]);
        });
      }
    }
  }, [selectedForm, selectedForm4]);

  React.useEffect(() => {
    async function fetchOldContractOwner(): Promise<void> {
      setFetchingOldContractOwner(true);
      const customerId = oldContract?.customerId || activeUser.userId;
      const fetchedOldContractOwner = await getCustomerDetails(customerId, activeUser.userId);

      if (fetchedOldContractOwner) {
        const user: SimpleUser = {
          userId: fetchedOldContractOwner.customerId,
          userEmail: fetchedOldContractOwner.email,
          userName: `${fetchedOldContractOwner.firstName} ${fetchedOldContractOwner.lastName}`,
          userFirstName: fetchedOldContractOwner.firstName,
          userLastName: fetchedOldContractOwner.lastName,
          userAddress: fetchedOldContractOwner.addresses || [],
          endpoint: 'ret',
        };
        dispatch(['changeOldContractOwner', user]);
        setFetchingOldContractOwner(false);
      }
    }

    if (oldContract && oldContract.customerId !== activeUser.userId) {
      fetchOldContractOwner();
    }
  }, [oldContract, activeUser]);

  React.useEffect(() => {
    if (selectedForm === 4 && selectedForm4 === 1 && useVaihtoturva && useVaihtoturva.length > 0) {
      const campaignId = import.meta.env.VITE_MANAGE_CONTRACTS_VAIHTOTURVA_CAMPAIGN_ID;

      if (!currentCampaigns.includes(parseInt(campaignId))) {
        const campaignPromise = fetchCampaignOmaLummePrices(campaignId);
        campaignPromise.then((campaigns) => {
          dispatch(['changeCampaigns', campaigns]);
        });
      }
    }
    if (
      selectedForm === 4 &&
      selectedForm4 === 3 &&
      oldContract &&
      oldContract?.activeDeliveries?.find((d) => tariffsUpgradableToGreenEnergy.includes(d.tariffNo))
    ) {
      const campaignId = import.meta.env.VITE_MANAGE_CONTRACTS_GREEN_ENERGY_CAMPAIGN_ID;

      // Add new contract options to same array to have Pörssisähkö Hintaturva as an option for extra services
      const campaignIdNewContracts = import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_CAMPAIGN_ID;

      if (!currentCampaigns.includes(parseInt(campaignId))) {
        const campaignPromise = fetchCampaignOmaLummePrices(campaignId);
        const campaignPromiseNewContracts = fetchCampaignOmaLummePrices(campaignIdNewContracts);
        campaignPromise.then((campaigns) => {
          campaignPromiseNewContracts.then((campaigns2) => {
            dispatch(['changeCampaigns', campaigns.concat(campaigns2)]);
          });
        });
      }
    }
  }, [selectedForm, selectedForm4, useVaihtoturva, oldContract]);

  // Update list of new contract options and change company from Lumme to Ekosähkö and vice versa based on existing contracts

  React.useEffect(() => {
    const lummeContractsFound = filteredPowerContracts.find((c) => c.contractGroup.contractGroupNo < 16 || c.contractGroup.contractGroupNo > 22);

    if (
      lummeContractsFound &&
      selectedForm === 4 &&
      selectedForm4 !== 3 &&
      oldContract &&
      (oldContract.contractGroup.contractGroupNo < 16 || oldContract.contractGroup.contractGroupNo > 22) &&
      !useVaihtoturva
    ) {
      const filteredDeliveries = oldContract.activeDeliveries
        ? oldContract.activeDeliveries.filter(
            (d) => !(d.elementName.toLowerCase().includes('vaihtoturva') || d.elementName.toLowerCase().includes('hyvitys')),
          )
        : [];

      const campaignId =
        new Date(filteredDeliveries[0].tariffToDate) >= new Date('2073-05-31')
          ? import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_FORNOW_CAMPAIGN_ID
          : import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_CAMPAIGN_ID;

      if (!currentCampaigns.includes(parseInt(campaignId))) {
        const campaignPromise = fetchCampaignOmaLummePrices(campaignId);
        campaignPromise.then((campaigns) => {
          dispatch(['changeCampaigns', campaigns]);
        });
      }
    }
    if (oldContract && lummeContractsFound && (selectedForm === 1 || selectedForm === 2)) {
      const filteredDeliveries = oldContract.activeDeliveries
        ? oldContract.activeDeliveries.filter(
            (d) => !(d.elementName.toLowerCase().includes('vaihtoturva') || d.elementName.toLowerCase().includes('hyvitys')),
          )
        : [];

      const campaignId =
        new Date(filteredDeliveries[0].tariffToDate) >= new Date('2073-05-31')
          ? import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_FORNOW_CAMPAIGN_ID
          : import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_CAMPAIGN_ID;

      if (!currentCampaigns.includes(parseInt(campaignId))) {
        const campaignPromise = fetchCampaignOmaLummePrices(campaignId);
        campaignPromise.then((campaigns) => {
          dispatch(['changeCampaigns', campaigns]);
        });
      }
    }
  }, [filteredContracts, filteredPowerContracts, selectedForm, selectedForm4, oldContract]);

  // Update list of extra services

  React.useEffect(() => {
    if (newContract) {
      const templates = campaignOmaLummePrices.find(
        (campaign) => campaign.contractTemplateNo === parseInt(newContract.split('-')[0]),
      )?.addOnTemplates;

      if (templates) {
        dispatch(['changeTemplates', templates]);
      } else if (addOnTemplates.length > 0) {
        dispatch(['changeTemplates', []]);
      }
    }
    if (selectedForm4 === 3 && oldContract) {
      if (oldContract && oldContract.activeDeliveries?.length) {
        const filteredDeliveries = oldContract.activeDeliveries.filter(
          (d) => !(d.elementName.toLowerCase().includes('vaihtoturva') || d.elementName.toLowerCase().includes('hyvitys')),
        );
        const oldTariffNo = filteredDeliveries[0].tariffNo !== 23 ? filteredDeliveries[0].tariffNo : 1;

        const templates = campaignOmaLummePrices.find((campaign) => campaign.tariffs.find((t) => t.tariffNo === oldTariffNo))?.addOnTemplates;

        if (templates) {
          dispatch(['changeTemplates', templates]);
        } else if (addOnTemplates.length > 0) {
          dispatch(['changeTemplates', []]);
        }
      }
    }
  }, [campaignOmaLummePrices, newContract, selectedForm4, oldContract, filteredContracts, addOnTemplates]);

  // Reset contracts when switching forms
  React.useEffect(() => {
    dispatch(['changeOldContract', undefined]);
    dispatch(['newContract', undefined]);
  }, [selectedForm]);

  // Form validation

  React.useEffect(() => {
    const { paymentType, paymentTypeEbillValid, paymentTypeNewEmailValid } = payment;

    const oldContractPaymentType = oldContract
      ? filteredLedgers.find((l) => l.ledgerNo === oldContract?.ledgerNo)?.paymentType?.paymentTypeNo
      : undefined;

    if (selectedForm === 4 && oldContractPaymentType && paymentType === '') {
      dispatch(['changePayment', { ...payment, paymentType: 'paperMyAddress', paymentTypeEbillValid: true, paymentTypeNewEmailValid: true }]);
    }

    const paymentTypeValid = paymentType || oldContractPaymentType === 3 || oldContractPaymentType === 20 ? true : false;

    const terminateAddressValid =
      (selectedForm === 1 || selectedForm === 3) &&
      ((terminateLedgerAddress && validateAddress(terminateLedgerAddress)) || terminateLedgerUseCurrentAddress)
        ? true
        : false;


    const primaryAddressValid =
      (selectedForm === 1 || selectedForm === 3) && ((primaryAddress && validateAddress(primaryAddress)) || !changePrimaryAddress) ? true : false;

    validateContractManagementForm({
      selectedForm,
      selectedForm2,
      selectedForm4,
      oldContract,
      endDate,
      createNewContract,
      newAddress,
      primaryAddressValid,
      terminateAddressValid,
      startDate,
      newContract,
      extraServices,
      paymentTypeValid,
      paymentTypeEbillValid,
      paymentTypeNewEmailValid,
      termsAccepted,
      setFormValid,
    });
  }, [
    filteredLedgers,
    selectedForm,
    selectedForm2,
    selectedForm4,
    oldContract,
    endDate,
    createNewContract,
    newAddress,
    primaryAddress,
    changePrimaryAddress,
    startDate,
    newContract,
    extraServices,
    terminateLedgerAddress,
    terminateLedgerUseCurrentAddress,
    payment,
    termsAccepted,
  ]);

  React.useEffect(() => {
    if (oldContract?.contractCode === useVaihtoturva) {
      const type = oldContract?.activeDeliveries?.find((d) => d.tariffName.toLowerCase().includes('vaihtoturva'))?.tariffName.toLowerCase();

      if (type === 'vaihtoturva kerrostalo') {
        dispatch(['changeHouse', 'flat']);
      }
      if (type === 'vaihtoturva rivitalo') {
        dispatch(['changeHouse', 'row']);
      }
      if (type === 'vaihtoturva ok-talo') {
        dispatch(['changeHouse', 'detached']);
      }
      if (type === 'vaihtoturva ok-talo sähkö') {
        dispatch(['changeHouse', 'detached-electric-heating']);
      }
    }
  }, [oldContract, contracts, useVaihtoturva]);

  React.useEffect(() => {
    async function fetchMarketingPermissions(): Promise<void> {
      const permissions = await getMarketingPermissions(
        oldContractOwner && oldContractOwner.userId !== activeUser.userId ? oldContractOwner.userId : activeUser.userId,
      );
      setMarketingEmail(permissions.marketingEmail);
      setMarketingSMS(permissions.marketingSMS);
      if (permissions.marketingEmail || permissions.marketingSMS) {
        setMarketingPermissions(true);
      }
    }
    if (activeUser && !marketingPermissions) {
      fetchMarketingPermissions();
    }
  }, [activeUser, oldContractOwner, marketingPermissions]);

  const submitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (formPage === 0) {
      dispatch(['changePage', formPage + 1]);
      navigate('/contracts/manage#summary');
    }
    if (formPage === 1) {
      gaEventIfAllowed({
        category: `Hallinnoi sopimuksia - ${t(`manageContracts:manageContracts.StartOption${selectedForm}Title`)}`,
        action: `Sopimuslomake lähetetty (${t(`manageContracts:manageContracts.StartOption${selectedForm}Title`)})`,
      });
      if (selectedForm === 2) {
        gaEventIfAllowed({
          category: `Hallinnoi sopimuksia - ${t(`manageContracts:manageContracts.StartOption2Sub${selectedForm2}Title`)}`,
          action: 'Lomake lähetetty',
        });
      }
      if (selectedForm === 4) {
        gaEventIfAllowed({
          category: `Hallinnoi sopimuksia - ${t(`manageContracts:manageContracts.StartOption4Sub${selectedForm4}Title`)}`,
          action: 'Lomake lähetetty',
        });
      }

      setFormSubmitting(true);

      const user = oldContractOwner && oldContractOwner.userId !== activeUser.userId ? oldContractOwner : activeUser;

      const activeUserAddress = user.userAddress[0];

      let activeMeteringPointAddress: ContractManagementFrendsAddress | boolean = false;

      activeMeteringPointAddress =
        activeUserAddress && activeUserAddress.customerId !== 0 && activeUserAddress.streetName !== ''
          ? createContractManagementFrendsAddress(activeUserAddress)
          : activeMeteringPointAddress
            ? createContractManagementFrendsAddress(activeMeteringPointAddress)
            : false;

      if (!createNewContract && oldContract && oldContract.meteringPoint) {
        activeMeteringPointAddress = createContractManagementFrendsAddress(oldContract.meteringPoint);
      }

      if ((createNewContract || selectedForm === 2) && newAddress) {
        activeMeteringPointAddress = createContractManagementFrendsAddress(newAddress);
      }

      const generatedCustomerAddress =
        activeUserAddress && activeUserAddress.customerId !== 0 && activeUserAddress.streetName !== ''
          ? createContractManagementFrendsAddress(activeUserAddress)
          : activeMeteringPointAddress
            ? createContractManagementFrendsAddress(activeMeteringPointAddress)
            : false;

      if (startDate && activeMeteringPointAddress && generatedCustomerAddress) {
        const form: ContractManagementFormObject = {
          customerId: user.userId,
          firstName: user.userFirstName,
          lastName: user.userLastName,
          email: user.userEmail,
          personalId: ssn,
          startDate: startDate.toISOString(),
          createdDate: new Date().toISOString(),
          mobileNumber: user.userNumber,
          customerAddress: generatedCustomerAddress,
          paymentTypeNo: payment.paymentType ? (payment.paymentType === 'eBill' ? 3 : paymentTypeToNumber(payment.paymentType)) : undefined,
          invoicingGroup: 5, // hard-coded 12 invoices per year
          meteringPoints: [
            {
              startDate: startDate?.toISOString(),
              address: activeMeteringPointAddress,
            },
          ],
          measurementType: 1, // hard-coded yleissähkö
          channelCode: 27, // hard-coded OmaLumme online self service
          bankName: payment.paymentTypeEbillBank,
          marketingEmail: marketingEmail,
          marketingSms: marketingSMS,
        };

        if (selectedForm !== 2 && oldContract) {
          form.contractNo = oldContract.contractNo;
          form.customerId = oldContract.customerId;
        }
        if (selectedForm === 4 && selectedForm4 === 1 && oldContract && oldContract.preferredMeasuringType) {
          if (parseInt(oldContract.preferredMeasuringType) === 2) {
            form.measurementType = 2;
          } else if (parseInt(oldContract.preferredMeasuringType) === 3) {
            form.measurementType = 3;
          } else {
            form.measurementType = 1;
          }
        }

        let newTariff: OmaLummeTariff | undefined = undefined;
        let offers: OmaLummeTariffElement[] | undefined = undefined;

        const selectedNewContract = newContract;

        if (selectedNewContract) {
          const contractTemplateNo = parseInt(selectedNewContract.split('-')[0]);
          const newContract = campaignOmaLummePrices.find((c) => c.contractTemplateNo === contractTemplateNo);
          newTariff = newContract?.tariffs.find((t) => t.tariffNo.toString() === selectedNewContract.split('-')[1]);
          offers = newTariff?.tariffElements.filter((element) => (element.elementNo === 21 || element.elementNo === 28) && element.discountInfo);
          form.contractTemplateNo = contractTemplateNo;
        }

        if ((selectedForm === 1 || selectedForm === 3) && endDate) {
          form.endDate = addDays(endDate, 1).toISOString();
        }

        if (selectedForm === 1 && createNewContract && newContract === '0-0' && oldContract) {
          form.transferContractNo = oldContract.contractNo;
        }
        if ((selectedForm === 1 || selectedForm === 3) && oldContract && (terminateLedgerAddress || terminateLedgerUseCurrentAddress)) {
          const oldAddress = activeUser.userAddress[0];
          const terminateAddress = terminateLedgerUseCurrentAddress ? oldAddress : terminateLedgerAddress;

          if (terminateAddress && terminateAddress.streetName && terminateAddress.zipCode && terminateAddress.cityName) {
            const formTerminateLedgerAddress = {
              streetName: terminateAddress.streetName,
              houseNumber: terminateAddress.houseNumber,
              houseLetter: terminateAddress.houseLetter,
              residence: terminateAddress.residence,
              zipCode: terminateAddress.zipCode,
              cityName: terminateAddress.cityName,
              ledgerNo: oldContract.ledgerNo,
              careOf: terminateAddress.careOf,
            };

            form.terminateLedgerAddress = formTerminateLedgerAddress;
          }
        }

        if (selectedForm === 1 && createNewContract && selectedNewContract === '0-0' && oldContract) {
          form.transferContractNo = oldContract.contractNo;
        }

        if (selectedForm === 2 && (selectedForm2 === 1 || selectedForm2 === 2)) {
          form.reasonNewContractNo = selectedForm2;
        }

        if (newTariff && offers && offers?.length > 0) {
          const tariffNo = newTariff.tariffNo;
          form.specialTariffs = offers.map((o) => {
            return { tariffNo: tariffNo, tariffElementNo: o.elementNo, duration: o.discountInfo?.duration ?? 0 };
          });
        }

        if (extraServices.length > 0) {
          form.addonProductTemplateNos = extraServices;
          form.addonProductStartDate = format(new Date(), 'yyyy-MM-dd');
        }

        if (selectedForm === 1 && createNewContract && changePrimaryAddress && changeAddressDate) {
          const newCustomerAddress = {
            ...activeMeteringPointAddress,
            startDate: changeAddressDate.toISOString(),
          };

          form.customerAddress = newCustomerAddress;
        }
        if (selectedForm === 1 && !createNewContract && changePrimaryAddress && primaryAddress && endDate) {
          const newPrimary = createContractManagementFrendsAddress(primaryAddress);
          if (newPrimary) {
            const newCustomerAddress = {
              ...newPrimary,
              startDate: endDate.toISOString(),
            };

            form.customerAddress = newCustomerAddress;
          }
        }

        if (payment.paymentType === 'paperMyAddress' || (payment.paymentType === 'paperNewAddress' && payment.paymentTypeNewAddress)) {
          let generatedLedgerAddress: ContractManagementFrendsAddress | false = false;

          if (payment.paymentType === 'paperMyAddress') {
            if (changePrimaryAddress && primaryAddress) {
              generatedLedgerAddress = createContractManagementFrendsAddress(primaryAddress);
            } else {
              generatedLedgerAddress = createContractManagementFrendsAddress(activeUser.userAddress[0]);
            }
          }
          if (payment.paymentType === 'paperNewAddress' && payment.paymentTypeNewAddress) {
            generatedLedgerAddress = createContractManagementFrendsAddress(payment.paymentTypeNewAddress);
          }

          if (generatedLedgerAddress) {
            form.ledgerAddress = generatedLedgerAddress;
          }
        }

        if (
          multipleOwners &&
          multipleOwnersNewAddress &&
          multipleOwnersNewAddress.ssn &&
          multipleOwnersNewAddress.firstname &&
          multipleOwnersNewAddress.lastname &&
          multipleOwnersNewAddress.mobileNumber
        ) {
          const additionalOwners: ContractManagementFrendsAdditionalOwner[] = [];
          const additionalOwnerAddress = createContractManagementFrendsAddress(multipleOwnersNewAddress);

          if (additionalOwnerAddress) {
            additionalOwners.push({
              personalId: multipleOwnersNewAddress.ssn.trim(),
              firstName: multipleOwnersNewAddress.firstname,
              lastName: multipleOwnersNewAddress.lastname,
              mobileNumber: multipleOwnersNewAddress.mobileNumber,
              address: additionalOwnerAddress,
            });

            form.additionalOwners = additionalOwners;
          }
        }

        if (payment.paymentType === 'emailNewAddress') {
          form.billingEmail = payment.paymentTypeNewEmail;
        }

        const continueWithOldContract = newContract === '0-0' ? true : false;

        const useCase = selectedFormToUseCase(selectedForm, selectedForm2, selectedForm4, createNewContract, continueWithOldContract);

        if (selectedForm === 4 && (selectedForm4 === 1 || selectedForm4 === 3) && oldContract) {
          form.invoicingGroup = oldContract.billingGroup.billingGroupNo;
        }
        if (selectedForm === 4 && selectedForm4 === 3 && oldContract) {
          let contractTemplateNo: number | undefined = undefined;

          if (oldContract.contractGroup.contractGroupNo === 16) {
            contractTemplateNo = 1;
          }
          if (oldContract.contractGroup.contractGroupNo === 1) {
            contractTemplateNo = 8;
          }
          if (oldContract.contractGroup.contractGroupNo === 7) {
            contractTemplateNo = 26;
          }

          form.contractTemplateNo = contractTemplateNo;
          form.paymentTypeNo = filteredLedgers.find((l) => l.ledgerNo === oldContract.ledgerNo)?.paymentType?.paymentTypeNo;
        }

        if (selectedForm === 4 && selectedForm4 === 2) {
          if (payment.paymentType && oldContract) {
            const paymentTypeNo = paymentTypeToNumber(payment.paymentType);
            let bankId: number | undefined = undefined;
            let email: string | undefined = undefined;
            if (payment.paymentType === 'eBill' && payment.paymentTypeEbillBank) {
              bankId = bankNameToId(payment.paymentTypeEbillBank);
            }
            if (payment.paymentType === 'emailMyAddress') {
              email = activeUser.userEmail;
            }
            if (payment.paymentType === 'emailNewAddress') {
              email = payment.paymentTypeNewEmail;
            }

            const paymentTypeRequest = await putPaymentType(activeUser.userId, oldContract.ledgerNo, paymentTypeNo, bankId, email);
            if (paymentTypeRequest.status === 200) {
              dispatch(['changePage', 2]);
              setFormSubmitting(false);
              navigate('/contracts/manage#thanks');
            }
          }
        } else {
          const postContractManagementRequest = await postContractManagement(form, useCase, form.customerId, activeUser.userId);
          if (postContractManagementRequest.status === 200) {
            dispatch(['changePage', 2]);
            setFormSubmitting(false);
            navigate('/contracts/manage#thanks');
          }
        }
      }
    }
  };

  const hasExtraServiceContracts = filteredContracts.find((c) => c.activeDeliveries && extraServicesAvailable(c.activeDeliveries));

  /* To be enabled in LE-262
  || existingExtraServices(c.activeDeliveries).length > 0)); */

  const user = oldContractOwner && oldContractOwner.userId !== activeUser.userId ? oldContractOwner : activeUser;

  if (customerType !== 'business' && users.activeUser.userId !== 0) {
    return (
      <Box>
        <h1 aria-live="polite">{t('contracts:contracts.activeContractsTitle')}</h1>
        <SubMenu />
        {(formPage === 0 || formPage === 1) && <h2 aria-live="polite">{t('manageContracts:manageContracts.Title')}</h2>}
        {formPage === 0 && <p>
          <Trans
            i18nKey={`manageContracts:manageContracts.Ingress${disableManageContracts ? 'Oomi' : ''}`}
            values={{
              oomiUrl: 'https://oomi.fi/sahko',
              readMoreUrl: 'https://www.lumme-energia.fi/oomi'
            }}
          >
            .
            <a href="https://oomi.fi/sahko" target="_blank" rel="noopener noreferrer"></a>
            <a href="https://www.lumme-energia.fi/oomi" target="_blank" rel="noopener noreferrer"></a>
          </Trans>
        </p>}

        {formPage === 0 && (
          <div className="myinfo accordion col">
            {!customerFetched && <Loading />}
            {/* {customerFetched && noAddresses && <p>{t(`myInfo:customerInfo.noAddresses`)}</p>} */}
            {customerFetched && <CustomerDetails customer={users.activeUser} manageContracts={true} type="parent" />}
          </div>
        )}

        <form className="manage-contracts" onSubmit={submitForm}>
          {formPage === 0 && customerFetched && (
            <>
              <Start filteredPowerContracts={filteredPowerContracts} hasExtraServiceContracts={hasExtraServiceContracts ? true : false} />

              {!disableManageContracts && selectedForm === 1 && <MoveBase />}
              {!disableManageContracts && selectedForm === 2 && selectedForm2 !== 0 && <New />}
              {disableManageContracts && selectedForm === 3 && <Terminate />}
              {!disableManageContracts && selectedForm === 4 && selectedForm4 !== 0 && <ChangeBase />}

              {((selectedForm === 1 && createNewContract !== undefined && changePrimaryAddress !== undefined) ||
                (selectedForm === 2 && selectedForm2 !== 0) ||
                selectedForm === 3 ||
                (selectedForm === 4 && selectedForm4 !== 0 && oldContract && !(selectedForm4 === 3 && addOnTemplates.length === 0 && houseType))) &&
                !(selectedForm4 && selectedForm4 !== 2 && houseType === '') && (
                  <>
                    <section className="manage-contracts-step stepConfirmation">
                      <h3>{t('manageContracts:manageContracts.ConfirmTitle')}*</h3>

                      <p>
                        <Trans
                          i18nKey="manageContracts:manageContracts.ConfirmBody"
                          values={{
                            termsLink: t(`manageContracts:manageContracts.ConfirmBodyTermsLink`),
                            privacyLink: t(`manageContracts:manageContracts.ConfirmBodyPrivacyLink`),
                          }}
                        >
                          .<a href="https://www.lumme-energia.fi/sahkosopimus/sopimusehdot" target="_blank" rel="noopener noreferrer"></a>{' '}
                          <a href="https://www.lumme-energia.fi/tietosuojaseloste" target="_blank" rel="noopener noreferrer"></a>
                        </Trans>
                      </p>

                      <div className="form-items">
                        {selectedForm !== 3 && !(selectedForm === 1 && !createNewContract) && (
                          <ManageContractsCheckbox
                            radioID="confirm1"
                            onChangeFunc={(): void => setTermsAccepted(!termsAccepted)}
                            checkedComparison={termsAccepted}
                            label={`${t('manageContracts:manageContracts.ConfirmAccept')}`}
                          />
                        )}
                        {(selectedForm === 3 || (selectedForm === 1 && !createNewContract)) && (
                          <p>{t('manageContracts:manageContracts.ConfirmThanks')}</p>
                        )}

                        {!marketingPermissions && (
                          <>
                            {selectedForm !== 3 && <p>{t(`manageContracts:manageContracts.ConfirmMarketingBody`)}</p>}
                            {selectedForm === 3 && <p>{t(`manageContracts:manageContracts.ConfirmMarketingBodyForm3`)}</p>}
                            {user.userEmail && (
                              <ManageContractsCheckbox
                                radioID="confirm2"
                                onChangeFunc={(): void => setMarketingEmail(!marketingEmail)}
                                checkedComparison={marketingEmail}
                                label={`${t('manageContracts:manageContracts.ConfirmMarketingEmail')} <strong>${user.userEmail}</strong>`}
                              />
                            )}
                            {user.userNumber && (
                              <ManageContractsCheckbox
                                radioID="confirm3"
                                onChangeFunc={(): void => setMarketingSMS(!marketingSMS)}
                                checkedComparison={marketingSMS}
                                label={`${t('manageContracts:manageContracts.ConfirmMarketingSMS')} <strong>${user.userNumber}</strong>`}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </section>
                    {fetchingOldContractOwner && <Loading />}
                    {!fetchingOldContractOwner && <Footer formSubmitting={formSubmitting} formValid={formValid} />}
                  </>
                )}
            </>
          )}

          {formPage === 1 && (
            <>
              <ManageContractsSummary
                campaigns={campaignOmaLummePrices}
                marketingPermissions={marketingPermissions}
                marketingEmail={marketingEmail}
                marketingSMS={marketingSMS}
              />
              <Footer formSubmitting={formSubmitting} formValid={formValid} />
            </>
          )}

          {formPage === 2 && <ThankYou />}
        </form>
      </Box>
    );
  }
  // B2B users don't have contract management
  return <div></div>;
};

export default ManageContracts;
